import { useParams } from "react-router-dom";
import { Flame, Link2 } from "lucide-react";
import { useSharedNote } from "@/api/hooks";
import type { SharedNote } from "@/api/types";
import { isApiError } from "@/api/client";
import { formatDateTime } from "@/lib/utils";
import { useTheme } from "@/lib/theme";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";
import { ErrorState } from "@/components/States";
import { Mark } from "@/components/Mark";
import { PageSkeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Moon, Sun, Laptop } from "lucide-react";

export default function SharedNotePage() {
  const { token } = useParams<{ token: string }>();
  const shared = useSharedNote(token);
  const theme = useTheme();
  const ThemeIcon = theme.mode === "system" ? Laptop : theme.mode === "dark" ? Moon : Sun;

  return (
    <div className="min-h-app">
      <header className="sticky top-[env(safe-area-inset-top)] z-20 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-13 max-w-3xl items-center gap-2 px-4">
          <Mark className="size-6 shrink-0" />
          <span className="text-sm font-semibold tracking-tight">Minne</span>
          <span className="ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">
            <Link2 className="size-3" /> shared note
          </span>
          <div className="flex-1" />
          <Button variant="ghost" size="icon-sm" onClick={theme.cycle} aria-label={`Theme: ${theme.mode}`}>
            <ThemeIcon />
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">
        {shared.isLoading && <PageSkeleton />}
        {shared.isError &&
          (isApiError(shared.error, 404) || isApiError(shared.error, 410) ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed px-6 py-12 text-center">
              <Flame className="mb-3 size-6 text-muted-foreground" />
              <div className="font-medium">This link is no longer available</div>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                {isApiError(shared.error, 410) ? "It was a single-use link and has already been opened, or it expired." : "It may have been single-use, expired, or revoked. Ask the owner for a new one."}
              </p>
            </div>
          ) : (
            <ErrorState error={shared.error} />
          ))}
        {shared.data && (
          <article>
            {/* The header shows the title; skip the note's leading H1 block, as the note page does. */}
            <header className="mb-6">
              <h1 className="text-3xl font-semibold leading-tight tracking-tight">{shared.data.title || "Untitled"}</h1>
              {shared.data.updatedAt && <div className="mt-2 text-xs text-muted-foreground">updated {formatDateTime(shared.data.updatedAt)}</div>}
            </header>
            <BlockRenderer blocks={bodyBlocks(shared.data.blocks ?? [])} />
            <footer className="mt-12 border-t pt-4 text-xs text-muted-foreground">Read-only view. Shared from Minne.</footer>
          </article>
        )}
      </main>
    </div>
  );
}

function bodyBlocks(blocks: SharedNote["blocks"]): SharedNote["blocks"] {
  const first = blocks[0];
  return first && first.type === "heading" && first.depth === 1 ? blocks.slice(1) : blocks;
}
